/**
 * NewsListPage
 */

import React, { useState } from 'react';
import { NewsListPageModel } from 'types/pages';
import { useUrlQuery, useSearchResult } from 'hooks';
import { getActiveFilters } from 'common/helpers';

import { NewsPageCardModel } from 'types/cards';

import { Content, ResultsContainer } from './NewsListPage.styles';

import EpiContentArea from 'components/EpiContentArea';
import LoadMorePagination from 'components/LoadMorePagination';
import { NewsPageCard } from 'components/Cards';
import { Grid, Cell, GridProps } from 'components/Grid';
import Breadcrumb from 'components/Breadcrumb';

import SearchForm from 'components/SearchForm';

/** Page template for a newslist page. */
const NewsListPage: React.FC<NewsListPageModel> = ({
	breadcrumbs,
	topContentArea,
	filter,
	pagination,
	items,
	hideImages,
	showIntroduction,
	translations,
	resultsPhrase,
	contentArea,
	showAsList,
	_properties = {},
}) => {
	const [action, setAction] = useState('');
	const activeFilters = getActiveFilters(filter);
	const [values, setValues] = useState<any>({ ...activeFilters });

	const [currentPage, setCurrentPage] = useState<number>(
		pagination.currentPage
	);

	const [updateQuery, response, loading, error] = useUrlQuery({
		...values,
		page: currentPage,
	});

	const [
		filters,
		paginations,
		results,
		responseResultsPhrase,
	] = useSearchResult(
		filter,
		pagination,
		items,
		response,
		action,
		resultsPhrase
	);

	const paginationPhrase = translations.paginationPhrase.split('{0}');

	return (
		<main id="main-content">
			<Breadcrumb
				links={breadcrumbs}
				label={translations.breadcrumbsLabel}
				withBorder
			/>

			<EpiContentArea
				{..._properties?.topContentArea}
				content={topContentArea}
				isPageHeader={true}
			/>

			<SearchForm
				translations={{
					errorText: translations.errorText,
					clearFilterText: translations.clearFilterText,
					clearAllFiltersText: translations.clearAllFiltersText,
					filterHeading: translations.filterHeading,
					hiddenSubmitText: translations.hiddenSubmitText,
				}}
				resultsPhrase={responseResultsPhrase}
				_properties={_properties}
				loading={loading}
				error={error}
				filter={filters}
				pagination={paginations}
				values={values}
				handleSetAction={setAction}
				handleSetCurrentPage={setCurrentPage}
				handleSetValues={setValues}
				handleUpdateQuery={updateQuery}
				displayHiddenSubmitButton
			/>

			<Content>
				<ResultsContainer id="news-list">
					{results && results.length > 0 && (
						<Grid
							inner
							manualGutter={
								showAsList ? GridProps.smallGap : GridProps.mediumGap
							}
						>
							{results.map((item: NewsPageCardModel, i: number) => (
								<Cell span={showAsList ? 12 : 4} key={i}>
									<NewsPageCard
										{...item}
										id={item.id}
										hideImages={hideImages}
										backgroundColor="white"
										showAsList={showAsList}
										index={i}
										showIntroduction={showIntroduction}
									/>
								</Cell>
							))}
						</Grid>
					)}
				</ResultsContainer>

				<LoadMorePagination
					visible={paginations.currentPage < paginations.pageCount}
					loading={loading}
					buttonText={translations.showMoreButtonLabel}
					totalDisplay={paginations.totalDisplay}
					totalHits={paginations.total}
					paginationPhrase={paginationPhrase}
					showAsList={showAsList}
					onClick={() => {
						setCurrentPage((page) => page + 1);
						updateQuery({ ...values, page: currentPage + 1 });
						setAction('add');
					}}
				/>
			</Content>

			<EpiContentArea {..._properties?.contentArea} content={contentArea} />
		</main>
	);
};

export default NewsListPage;
